import { getAuthToken } from '../../services/auth/tokenStorage';
import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Image, ScrollView, Alert } from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { NavigationProp, useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../../config';
import logo from '../../assets/logo.png';
import accountIcon from '../../assets/icons/linked-accounts.png';
import backIcon from '../../assets/icons/back.png';

import { createLogger } from '../../utils/logger';
const logger = createLogger('LinkedAccounts');

interface LinkedAccountsScreenProps {
  navigation: NavigationProp<any>;
}

interface Relationship {
  id: string;
  secondaryUser: {
    firstName: string;
    lastName: string;
    email: string;
  };
  role: 'successor' | 'keyholder';
  status: string;
}

interface Invitation {
  id: string;
  inviteeEmail: string;
  role: 'successor' | 'keyholder';
  status: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  validationQuestion?: string;
  createdAt: string;
  expiresAt: string;
}

const LinkedAccountsScreen: React.FC<LinkedAccountsScreenProps> = ({ navigation }) => {
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);

  // Refetch data when screen comes into focus
  useFocusEffect(
    React.useCallback(() => {
      fetchData();
    }, [])
  );

  const fetchData = async () => {
    try {
      const token = await getAuthToken();
      if (!token) {
        Alert.alert('Error', 'Please log in again');
        setLoading(false);
        return;
      }

      // Fetch relationships with role=primary to get full relationship data with secondaryUser
      const relationshipsResponse = await fetch(`${API_URL}/api/relationships?role=primary`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (relationshipsResponse.ok) {
        const relationshipsData = await relationshipsResponse.json();
        logger.debug('Relationships data:', JSON.stringify(relationshipsData, null, 2));

        // Handle the response - could be array format or combined {successors: [], keyholders: []} format
        if (relationshipsData.relationships) {
          if (Array.isArray(relationshipsData.relationships)) {
            // Array format - direct list of relationships with secondaryUser data
            setRelationships(relationshipsData.relationships);
          } else if (relationshipsData.relationships.successors || relationshipsData.relationships.keyholders) {
            // Combined format - transform to array format
            // Backend returns both active relationships AND pending invitations in one response
            logger.debug('Processing combined format...');
            logger.debug('Successors from API:', relationshipsData.relationships.successors);
            logger.debug('Keyholders from API:', relationshipsData.relationships.keyholders);

            // Transform and separate active vs pending
            const allItems = [
              ...(relationshipsData.relationships.successors || []).map((s: any) => ({
                id: s.id,
                secondaryUser: {
                  firstName: s.name?.split(' ')[0] || '',
                  lastName: s.name?.split(' ').slice(1).join(' ') || '',
                  email: s.email,
                },
                role: 'successor' as const,
                status: s.status,
              })),
              ...(relationshipsData.relationships.keyholders || []).map((k: any) => ({
                id: k.id,
                secondaryUser: {
                  firstName: k.name?.split(' ')[0] || '',
                  lastName: k.name?.split(' ').slice(1).join(' ') || '',
                  email: k.email,
                },
                role: 'keyholder' as const,
                status: k.status,
              })),
            ];

            // Active relationships go to relationships state
            const activeRelationships = allItems.filter((r: any) => r.status === 'active');
            logger.debug('Active relationships:', activeRelationships);
            setRelationships(activeRelationships);

            // Pending ones go to invitations state (transform to invitation format)
            const pendingItems = allItems.filter((r: any) => r.status === 'pending');
            const pendingAsInvitations = pendingItems.map((p: any) => ({
              id: p.id,
              inviteeEmail: p.secondaryUser.email,
              role: p.role,
              status: 'pending',
              firstName: p.secondaryUser.firstName,
              lastName: p.secondaryUser.lastName,
            }));
            logger.debug('Pending as invitations:', pendingAsInvitations);
            setInvitations(pendingAsInvitations);

            // Skip the separate invitations fetch since we got everything from combined response
            return;
          } else {
            logger.warn('Unexpected relationships format:', relationshipsData.relationships);
            setRelationships([]);
          }
        } else if (Array.isArray(relationshipsData)) {
          setRelationships(relationshipsData);
        } else {
          logger.warn('Unexpected relationships response structure:', relationshipsData);
          setRelationships([]);
        }
      } else {
        logger.error('Failed to fetch relationships, status:', relationshipsResponse.status);
        setRelationships([]);
      }

      // Fetch pending invitations separately
      const invitationsResponse = await fetch(`${API_URL}/api/invitations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (invitationsResponse.ok) {
        const invitationsData = await invitationsResponse.json();
        logger.debug('Invitations data:', invitationsData);

        // Handle different response structures
        if (Array.isArray(invitationsData)) {
          setInvitations(invitationsData);
        } else if (invitationsData.invitations && Array.isArray(invitationsData.invitations)) {
          setInvitations(invitationsData.invitations);
        } else if (invitationsData.data && Array.isArray(invitationsData.data)) {
          setInvitations(invitationsData.data);
        } else {
          logger.warn('Unexpected invitations response structure:', invitationsData);
          setInvitations([]);
        }
      } else {
        logger.error('Failed to fetch invitations, status:', invitationsResponse.status);
        setInvitations([]);
      }
    } catch (error) {
      logger.error('Error fetching data:', error);
      setRelationships([]);
      setInvitations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAccountPress = (relationship: Relationship) => {
    if (relationship.role === 'successor') {
      navigation.navigate('SuccessorDetails', {
        successorId: relationship.id,
        successor: {
          id: relationship.id,
          name: `${relationship.secondaryUser.firstName} ${relationship.secondaryUser.lastName}`,
          email: relationship.secondaryUser.email,
          role: relationship.role,
          status: relationship.status
        }
      });
    } else if (relationship.role === 'keyholder') {
      navigation.navigate('KeyHolderDetails', {
        relationshipId: relationship.id,
        keyholder: {
          id: relationship.id,
          name: `${relationship.secondaryUser.firstName} ${relationship.secondaryUser.lastName}`,
          email: relationship.secondaryUser.email,
          role: relationship.role,
          status: relationship.status
        }
      });
    }
  };

  const handleInvitationPress = (invitation: Invitation) => {
    if (invitation.role === 'successor') {
      navigation.navigate('SuccessorDetails', {
        invitationId: invitation.id,
        isPending: true,
        successor: {
          id: invitation.id,
          inviteeEmail: invitation.inviteeEmail,
          firstName: invitation.firstName,
          lastName: invitation.lastName,
          phone: invitation.phone,
          validationQuestion: invitation.validationQuestion,
          role: invitation.role,
          status: invitation.status,
          createdAt: invitation.createdAt,
          expiresAt: invitation.expiresAt
        }
      });
    } else if (invitation.role === 'keyholder') {
      navigation.navigate('KeyHolderDetails', {
        invitationId: invitation.id,
        isPending: true,
        keyholder: {
          id: invitation.id,
          inviteeEmail: invitation.inviteeEmail,
          firstName: invitation.firstName,
          lastName: invitation.lastName,
          phone: invitation.phone,
          validationQuestion: invitation.validationQuestion,
          role: invitation.role,
          status: invitation.status,
          createdAt: invitation.createdAt,
          expiresAt: invitation.expiresAt
        }
      });
    }
  };

  const cancelInvitation = async (invitationId: string) => {
    try {
      const token = await getAuthToken();
      if (!token) return;

      const response = await fetch(`${API_URL}/api/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        Alert.alert('Success', 'Invitation cancelled successfully');
        fetchData(); // Refresh the data
      } else {
        Alert.alert('Error', 'Failed to cancel invitation');
      }
    } catch (error) {
      logger.error('Error cancelling invitation:', error);
      Alert.alert('Error', 'Failed to cancel invitation');
    }
  };

  return (
    <View style={styles.container}>
      <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
        <Image source={backIcon} style={styles.backIcon} />
      </TouchableOpacity>
      <View style={styles.logoContainer}>
        <Image source={logo} style={styles.logo} resizeMode="contain" />
      </View>
      <Text style={styles.title}>My Successors & Keyholders</Text>
      <Text style={styles.description}>
        Below are the people you've invited as Successors or Keyholders. Tap on any person to edit their information or manage their access.
      </Text>
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
        {loading ? (
          <Text style={styles.emptyText}>Loading...</Text>
        ) : relationships.length === 0 && invitations.length === 0 ? (
          <Text style={styles.emptyText}>You haven't added any successors or keyholders yet.</Text>
        ) : (
          <>
            {/* Active Relationships */}
            {relationships
              .filter(relationship => relationship.secondaryUser) // Only show relationships with secondaryUser data
              .map((relationship) => (
                <TouchableOpacity
                  key={`rel-${relationship.id}`}
                  style={styles.accountCard}
                  onPress={() => handleAccountPress(relationship)}
                  activeOpacity={0.7}
                >
                  <Image
                    source={accountIcon}
                    style={[
                      styles.accountIcon,
                      relationship.role === 'successor' ? styles.successorIcon : styles.keyholderIcon
                    ]}
                  />
                  <View style={styles.accountInfo}>
                    <Text style={styles.accountName}>
                      {relationship.secondaryUser.firstName} {relationship.secondaryUser.lastName}
                    </Text>
                    <Text style={[
                      styles.accountType,
                      relationship.role === 'successor' ? styles.successorType : styles.keyholderType
                    ]}>
                      {relationship.role.charAt(0).toUpperCase() + relationship.role.slice(1)}
                    </Text>
                    <Text style={styles.accountEmail}>{relationship.secondaryUser.email}</Text>
                    <Text style={[styles.statusText, styles.activeStatus]}>✅ Active</Text>
                  </View>
                </TouchableOpacity>
              ))}

            {/* Pending Invitations */}
            {invitations.map((invitation) => (
              <TouchableOpacity 
                key={`inv-${invitation.id}`} 
                style={[styles.accountCard, styles.pendingCard]}
                onPress={() => handleInvitationPress(invitation)}
                activeOpacity={0.7}
              >
                <Image 
                  source={accountIcon} 
                  style={[
                    styles.accountIcon,
                    styles.pendingIcon,
                    invitation.role === 'successor' ? styles.successorIcon : styles.keyholderIcon
                  ]} 
                />
                <View style={styles.accountInfo}>
                  <Text style={styles.accountName}>
                    Invitation Sent
                  </Text>
                  <Text style={[
                    styles.accountType,
                    invitation.role === 'successor' ? styles.successorType : styles.keyholderType
                  ]}>
                    {invitation.role.charAt(0).toUpperCase() + invitation.role.slice(1)}
                  </Text>
                  <Text style={styles.accountEmail}>{invitation.inviteeEmail}</Text>
                  <Text style={[styles.statusText, styles.pendingStatus]}>⏳ Pending Acceptance</Text>
                </View>
              </TouchableOpacity>
            ))}
          </>
        )}

        {/* Return to Profile Button */}
        <TouchableOpacity
          style={styles.returnToProfileButton}
          onPress={() => navigation.navigate('ProfileDetails')}
          activeOpacity={0.7}
        >
          <Text style={styles.returnToProfileButtonText}>Return to Profile</Text>
        </TouchableOpacity>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
    alignItems: 'center',
    paddingTop: ms(40),
  },
  backButton: {
    padding: ms(5),
    alignSelf: 'flex-start',
    marginLeft: ms(10),
    marginTop: ms(10),
    marginBottom: 0,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  logoContainer: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: ms(10),
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#fff',
    marginTop: ms(10),
    marginBottom: ms(5),
    textAlign: 'center',
    width: '90%',
  },
  description: {
    fontSize: ms(16, 0.3),
    color: '#fff',
    textAlign: 'center',
    marginBottom: ms(20),
    width: '90%',
  },
  scrollView: {
    width: '100%',
  },
  scrollContent: {
    alignItems: 'center',
    paddingBottom: ms(40),
  },
  accountCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#EFF2F5',
    borderRadius: ms(20),
    padding: ms(20),
    marginBottom: ms(15),
    width: '92%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
  },
  accountIcon: {
    width: s(40),
    height: s(40),
    marginRight: ms(18),
    tintColor: '#F2994A',
  },
  accountInfo: {
    flex: 1,
  },
  accountName: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
  },
  accountType: {
    fontSize: ms(15, 0.3),
    color: '#29BFC0',
    fontWeight: '600',
    marginTop: ms(2),
  },
  accountEmail: {
    fontSize: ms(14, 0.3),
    color: '#888',
    marginTop: ms(2),
  },
  emptyText: {
    color: '#fff',
    fontSize: ms(16, 0.3),
    marginTop: ms(40),
    textAlign: 'center',
  },
  successorIcon: {
    tintColor: '#2d7a2d', // Green for successors
  },
  keyholderIcon: {
    tintColor: '#856404', // Orange for keyholders
  },
  successorType: {
    color: '#2d7a2d', // Green for successors
  },
  keyholderType: {
    color: '#856404', // Orange for keyholders
  },
  statusText: {
    fontSize: ms(12, 0.3),
    color: '#666',
    marginTop: ms(4),
    fontStyle: 'italic',
  },
  activeStatus: {
    color: '#2d7a2d',
    fontWeight: '600',
  },
  pendingStatus: {
    color: '#856404',
    fontWeight: '600',
  },
  pendingCard: {
    borderLeftWidth: ms(4),
    borderLeftColor: '#FFA500',
    opacity: 0.9,
  },
  pendingIcon: {
    opacity: 0.7,
  },
  returnToProfileButton: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(40),
    alignSelf: 'center',
    marginTop: ms(30),
    marginBottom: ms(20),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  returnToProfileButtonText: {
    color: '#29BFC0',
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
    textAlign: 'center',
  },
});

export default LinkedAccountsScreen; 